import { useEffect, useState, type ReactNode } from 'react'
import {
  reportApi,
  type QuarterReport,
  type ReportPeriod,
  type SummaryReport,
} from '../lib/api'
import { money, qtyToText } from '../lib/money'
import { BarTrend, RankBars, ShareBar } from './Charts'
import { PageHead } from './PageHead'

/**
 * Báo cáo của bộ Modern.
 *
 * Hai tab, vì đây là hai câu hỏi khác nhau:
 *   • Kinh doanh — "hôm nay/tuần này quán chạy thế nào", thứ chủ quán mở mỗi tối.
 *   • Thuế — "quý này phải nộp bao nhiêu, sổ đâu", thứ mở mỗi quý một lần nhưng là
 *     lý do người ta trả tiền cho phần mềm này.
 *
 * Gộp cả hai vào một trang thì trang đó dài mà không câu nào được trả lời cho ra hồn.
 */

const PERIODS: { id: ReportPeriod; label: string }[] = [
  { id: 'today', label: 'Hôm nay' },
  { id: '7d', label: '7 ngày' },
  { id: '30d', label: '30 ngày' },
  { id: '12m', label: '12 tháng' },
]

const PERIOD_PREV: Record<ReportPeriod, string> = {
  today: 'So với hôm qua',
  '7d': 'So với 7 ngày trước',
  '30d': 'So với 30 ngày trước',
  '12m': 'So với 12 tháng trước',
}

interface StatProps {
  label: string
  value: string
  now: number
  before: number
  hint: string
  tone: string
  icon: string
}

function Stat({ label, value, now, before, hint, tone, icon }: StatProps) {
  // Kỳ trước bằng 0 thì không có phần trăm nào đúng cả — nói thẳng là mới có kỳ này.
  const delta = before > 0 ? Math.round(((now - before) / before) * 100) : null

  return (
    <div className={`kpi is-${tone}`}>
      <span className="kpi-icon" aria-hidden="true">
        {icon}
      </span>
      <span className="kpi-key">{label}</span>
      <b className="kpi-val num">{value}</b>
      <span className="kpi-foot">
        {delta === null ? (
          <em>{before === 0 && now > 0 ? 'Kỳ trước chưa có số' : 'Chưa có số kỳ trước'}</em>
        ) : (
          <>
            <span className={`kpi-delta${delta < 0 ? ' is-down' : ''}`}>
              <span aria-hidden="true">{delta < 0 ? '↓' : '↑'}</span> {Math.abs(delta)}%
            </span>
            <em>{hint}</em>
          </>
        )}
      </span>
    </div>
  )
}

function Panel({ title, right, children }: { title: string; right?: ReactNode; children: ReactNode }) {
  return (
    <section className="panel">
      <header className="panel-head">
        <h2>{title}</h2>
        {right}
      </header>
      {children}
    </section>
  )
}

interface Props {
  /** Phần báo cáo thuế, dựng sẵn ở màn hình gọi — ở đây chỉ xếp chỗ cho nó. */
  taxTab: ReactNode
  quarter: QuarterReport | null
}

export function ReportBoard({ taxTab, quarter }: Props) {
  const [tab, setTab] = useState<'biz' | 'tax'>('biz')
  const [period, setPeriod] = useState<ReportPeriod>('today')
  const [data, setData] = useState<SummaryReport | null>(null)
  const [state, setState] = useState<'loading' | 'ready' | 'offline'>('loading')

  useEffect(() => {
    let alive = true
    setState('loading')

    reportApi
      .summary(period)
      .then((r) => {
        if (!alive) return
        setData(r)
        setState('ready')
      })
      .catch(() => alive && setState('offline'))

    return () => {
      alive = false
    }
  }, [period])

  const totalRevenue = data?.revenue ?? 0

  return (
    <div className="board report-board">
      <PageHead
        to="/"
        icon="📊"
        tone="is-green"
        title="Báo cáo"
        sub="Tổng quan hoạt động kinh doanh"
      />

      <div className="board-tools">
        <div className="seg seg-tabs">
          <button type="button" aria-pressed={tab === 'biz'} onClick={() => setTab('biz')}>
            Kinh doanh
          </button>
          <button type="button" aria-pressed={tab === 'tax'} onClick={() => setTab('tax')}>
            Thuế{quarter ? ` · Q${quarter.quarter}` : ''}
          </button>
        </div>
      </div>

      {tab === 'tax' ? (
        <div className="report-scroll">{taxTab}</div>
      ) : (
        <div className="report-scroll">
          <div className="chip-row zone-row">
            {PERIODS.map((p) => (
              <button
                key={p.id}
                type="button"
                className="chip"
                aria-pressed={period === p.id}
                onClick={() => setPeriod(p.id)}
              >
                {p.label}
              </button>
            ))}
          </div>

          {state === 'offline' ? (
            <div className="card warn-card">
              <p>
                Chưa lấy được báo cáo vì máy đang không có mạng. Số của hôm nay vẫn xem được ở
                màn hình chính — nó nằm ngay trên máy này.
              </p>
            </div>
          ) : !data ? (
            <div className="empty">
              <div className="empty-icon" aria-hidden="true">
                📊
              </div>
              <p>Đang tải…</p>
            </div>
          ) : (
            <>
              <div className="kpis">
                <Stat
                  label="Doanh thu"
                  value={`${money(data.revenue)}đ`}
                  now={data.revenue}
                  before={data.prevRevenue}
                  hint={PERIOD_PREV[period]}
                  tone="green"
                  icon="📈"
                />
                <Stat
                  label="Số hoá đơn"
                  value={String(data.orderCount)}
                  now={data.orderCount}
                  before={data.prevOrderCount}
                  hint={PERIOD_PREV[period]}
                  tone="blue"
                  icon="🧾"
                />
                <Stat
                  label="Trung bình mỗi đơn"
                  value={`${money(data.avgOrder)}đ`}
                  now={data.avgOrder}
                  before={data.prevAvgOrder}
                  hint={PERIOD_PREV[period]}
                  tone="violet"
                  icon="🪙"
                />
                <Stat
                  label="Tiền ra"
                  value={`${money(data.expense)}đ`}
                  now={data.expense}
                  before={0}
                  hint=""
                  tone="clay"
                  icon="🧺"
                />
              </div>

              <Panel
                title={period === 'today' ? 'Doanh thu theo giờ' : 'Doanh thu theo thời gian'}
                right={<span className="panel-note">Rê chuột vào cột để xem số đơn</span>}
              >
                <BarTrend points={data.points} />
              </Panel>

              <div className="panel-row">
                <Panel title="Top món bán chạy">
                  <RankBars
                    rows={data.top.map((t) => ({
                      name: t.name,
                      qtyText: qtyToText(t.qtyMilli),
                      revenue: t.revenue,
                    }))}
                  />
                </Panel>

                <Panel title="Doanh thu theo nhóm món">
                  <ShareBar slices={foldTail(data.categories)} total={totalRevenue} />
                </Panel>
              </div>

              <div className="panel-row">
                <Panel title="Bán tại bàn hay mang về">
                  <ShareBar slices={data.kinds} total={totalRevenue} />
                </Panel>

                <Panel title="Khách trả bằng gì">
                  <ShareBar slices={data.payments} total={totalRevenue} />
                </Panel>
              </div>

              <p className="foot-note">
                Số liệu cả quán, từ {vnDay(data.from)} tới {vnDay(data.to)}. Đơn đã huỷ không
                được tính.
              </p>
            </>
          )}
        </div>
      )}
    </div>
  )
}

/**
 * Gom đuôi dài vào một lát "Khác".
 *
 * Quá năm lát thì không còn màu nào phân biệt nổi với màu đã dùng, mà thêm màu mới chỉ
 * làm hai lát trông giống hệt nhau với người không phân biệt được màu. Năm lát lớn cộng
 * một lát gom là đủ trả lời câu "nhóm nào ra tiền nhất".
 */
function foldTail(rows: { name: string; revenue: number; orderCount: number }[]) {
  if (rows.length <= 5) return rows

  const head = rows.slice(0, 5)
  const tail = rows.slice(5)

  return [
    ...head,
    {
      name: `Khác (${tail.length} nhóm)`,
      revenue: tail.reduce((s, r) => s + r.revenue, 0),
      orderCount: tail.reduce((s, r) => s + r.orderCount, 0),
    },
  ]
}

/** "2026-09-10" → "10/09". Máy chủ trả ngày Việt Nam rồi, ở đây chỉ đổi cách viết. */
function vnDay(iso: string): string {
  const [y, m, d] = iso.split('-')
  return y && m && d ? `${d}/${m}` : iso
}
